import React, { useState, useCallback, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './OptimizedSlideshow.css';

const OptimizedSlideshow = React.memo(({ mediaItems }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [swiper, setSwiper] = useState(null);

    // Memoize media items to prevent unnecessary re-renders
    const memoizedMediaItems = useMemo(() => mediaItems || [], [mediaItems]);

    const renderMedia = useCallback((item, index) => {
        if (item.type === 'video') {
            return (
                <video 
                    controls 
                    className="optimized-media"
                    preload="metadata"
                    playsInline
                >
                    <source src={item.src} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        } else if (item.type === 'youtube') {
            return (
                <iframe
                    className="optimized-media"
                    src={item.src}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`Media ${index}`}
                    loading="lazy"
                />
            );
        } else {
            return (
                <img 
                    className="optimized-media"
                    src={item.src || item} 
                    alt={`Slide ${index}`}
                    loading="lazy"
                />
            );
        }
    }, []);

    const goToSlide = useCallback((index) => {
        if (swiper) {
            swiper.slideTo(index);
        }
    }, [swiper]);

    const nextSlide = useCallback(() => {
        if (swiper) {
            swiper.slideNext();
        }
    }, [swiper]);

    const prevSlide = useCallback(() => {
        if (swiper) {
            swiper.slidePrev();
        }
    }, [swiper]);

    const handleSlideChange = useCallback((swiper) => {
        setCurrentSlide(swiper.realIndex);
    }, []);

    // Early return after all hooks
    if (!memoizedMediaItems || memoizedMediaItems.length === 0) {
        return <div>No media to display</div>;
    }

    return (
        <div className="optimized-slideshow">
            <div className="slideshow-inner">
                {/* Slide Counter */}
                <div className="slide-counter">
                    {currentSlide + 1} / {memoizedMediaItems.length}
                </div>

                {/* Custom Navigation Arrows */}
                {memoizedMediaItems.length > 1 && (
                    <>
                        <div className="optimized-arrow-prev" onClick={prevSlide}></div>
                        <div className="optimized-arrow-next" onClick={nextSlide}></div>
                    </>
                )}

                <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    spaceBetween={0}
                    slidesPerView={1}
                    centeredSlides={true}
                    autoplay={memoizedMediaItems.length > 1 ? {
                        delay: 6000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                    } : false}
                    speed={400}
                    effect="slide"
                    loop={memoizedMediaItems.length > 1}
                    className="optimized-swiper"
                    onSwiper={setSwiper}
                    onSlideChange={handleSlideChange}
                    navigation={false}
                    pagination={false}
                    lazy={true}
                    watchSlidesProgress={true}
                    preloadImages={false}
                >
                    {memoizedMediaItems.map((item, index) => (
                        <SwiperSlide key={index}>
                            <div className="optimized-slide-content">
                                {renderMedia(item, index)}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                {/* Custom Pagination Dots */}
                {memoizedMediaItems.length > 1 && (
                    <div className="optimized-pagination">
                        {memoizedMediaItems.map((_, index) => (
                            <div
                                key={index}
                                className={`optimized-dot ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => goToSlide(index)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

OptimizedSlideshow.displayName = 'OptimizedSlideshow';

export default OptimizedSlideshow;